<script setup lang="ts">
import { computed } from 'vue'
import { usePermissionsStore } from '@/stores/permissions'
import type { Permission } from '@/composables/policies/permissions'

/**
 * Declarative permission gate. Renders its default slot only when the current
 * user is authorized, otherwise renders the (optional) #fallback slot.
 *
 * Forms:
 *   <Can do="group.view"> ... </Can>            single permission
 *   <Can :any="['a', 'b']"> ... </Can>          OR group
 *   <Can :all="['a', 'b']"> ... </Can>          AND group
 *   <Can :do="policy.canRemove(row)"> ... </Can> pre-resolved boolean (escape hatch)
 *
 * It is a frontend UX / defense-in-depth layer only — the backend Pundit
 * policies remain the authoritative check. Because it gates with v-if, a denied
 * slot is never mounted, so any onMounted fetch inside it never fires.
 *
 * Fails closed: with no recognized prop, nothing is rendered.
 *
 * Do NOT wrap definition-only children like PrimeVue's <Column> — DataTable
 * discovers columns from its own slot vnodes, so a Column inside <Can> is
 * silently dropped. Instead, leave the Column unconditional and gate the
 * buttons inside its #body template with <Can>, same as anywhere else — a
 * user with none of the row permissions just sees an empty cell.
 */
// Note: `do` accepts a Permission string OR a pre-resolved boolean. Vue coerces
// Boolean-typed props to `false` when absent, which would make an unset `do`
// indistinguishable from `:do="false"`. We default it to `undefined` so the
// `any` / `all` branches still apply when only those are passed.
const props = withDefaults(
  defineProps<{
    do?: Permission | boolean
    any?: Permission[]
    all?: Permission[]
  }>(),
  { do: undefined, any: undefined, all: undefined }
)

const store = usePermissionsStore()

const allowed = computed<boolean>(() => {
  // `do` is checked first, but only when it was actually given. A boolean prop
  // with no value defaults to `false` in Vue, so we can't distinguish "not
  // passed" from an explicit `:do="false"`. Guard with `do !== undefined` so an
  // unset `do` doesn't hijack the `any` / `all` branches.
  if (props.do !== undefined) {
    return typeof props.do === 'string' ? store.can(props.do) : props.do
  }
  if (props.any) return store.canAny(props.any)
  if (props.all) return store.canAll(props.all)
  return false
})
</script>

<template>
  <slot v-if="allowed" />
  <slot v-else name="fallback" />
</template>
